'use client';

import { Button, HorizontalDivider } from '@mage-ui/components';
import {
	button,
	flexWrapper,
	tableTdCartExpanded,
	tableThAction,
	text,
} from '@mage-ui/styled-system/recipes';
import { useContext, useState } from 'react';
import { formatPrice } from 'utils/amount';
import { useCart } from '@/components/context/CartProvider';
import { StaticTextContext } from '@/components/context/StaticTextProvider';
import { CartSummary } from '@/components/ui/data-display/cart-summary/CartSummary';
import { Feedback } from '@/components/ui/data-display/feedback/Feedback';
import { Table } from '@/components/ui/data-display/tables/Table';
import { TableTbody } from '@/components/ui/data-display/tables/TableTbody';
import { TableTd } from '@/components/ui/data-display/tables/TableTd';
import { TableTdAction } from '@/components/ui/data-display/tables/TableTdAction';
import { TableTh } from '@/components/ui/data-display/tables/TableTh';
import { TableThead } from '@/components/ui/data-display/tables/TableThead';
import { TableTr } from '@/components/ui/data-display/tables/TableTr';

export const TableCartBlock = ({ locale, summaryLink, shopLink }) => {
	const { st } = useContext(StaticTextContext);
	const [deletedItem, setDeletedItem] = useState(null);
	const { items, removeItem, addItem } = useCart();

	const handleDelete = (item) => {
		removeItem(item.cartItemId);
		setDeletedItem(item);
	};

	const handleUndo = () => {
		if (!deletedItem) return;

		addItem(deletedItem);
		setDeletedItem(null);
	};

	const deletedItemMessage = (deletedItem) => {
		return (
			deletedItem && (
				<div className={flexWrapper({ align: 'center', gap: 'gap400' })}>
					<p className={text({ size: '4' })}>
						{st('cart.product_prefix')} {deletedItem.name}{' '}
						{st('cart.product_deleted')}
					</p>
					<Button
						onClick={handleUndo}
						classNames={{ root: button({ variant: 'ghost', size: 's' }) }}
					>
						{st('cart.product_undo')}
					</Button>
				</div>
			)
		);
	};

	const totalPrice = items.reduce(
		(acc, item) => acc + Number(item.price ?? 0),
		0,
	);

	return items.length > 0 ? (
		<>
			<Table>
				<TableThead>
					<TableTr>
						<TableTh
							showSortButton={false}
							data={{ title: st('cart.product') }}
						/>
						<TableTh
							showSortButton={false}
							data={{ title: st('cart.price') }}
						/>
						<TableTh
							className={tableThAction()}
							showSortButton={false}
							data={{ title: st('cart.action') }}
						/>
					</TableTr>
				</TableThead>
				<TableTbody>
					{items.map((item) => (
						<TableTr key={item.cartItemId}>
							<TableTd
								className={tableTdCartExpanded()}
								data={{
									title: item.name,
								}}
							/>
							<TableTd data={{ title: formatPrice(item.price) }} />
							<TableTdAction
								data={{
									iconName: 'trash',
									action: () => handleDelete(item),
									title: st('cart.delete'),
								}}
							/>
						</TableTr>
					))}
				</TableTbody>
			</Table>

			<CartSummary
				data={{ price: formatPrice(totalPrice), summaryLink }}
				startSlot={deletedItemMessage(deletedItem)}
			/>
		</>
	) : (
		<>
			<Feedback
				data={{
					fields: {
						title: null,
						content: st('cart.cart_empty'),
						cta_text: st('cart.go_shop'),
						cta_link: shopLink,
					},
				}}
				language={locale}
			/>
			{deletedItem && (
				<div className={flexWrapper({ direction: 'column', gap: 'gap300' })}>
					<HorizontalDivider />
					{deletedItemMessage(deletedItem)}
				</div>
			)}
		</>
	);
};
